import UserIcon from "@redotech/redo-icons/user-01.svg";
import { RedoAvatar } from "@redotech/redo-web/arbiter-components/avatar/redo-avatar";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useMemo } from "react";
import type { SupportTicketPresenceRecord } from "../db/collections";

const DEFAULT_MAX_VISIBLE_AVATARS = 3;

export const PresenceAvatarGroup = memo(function PresenceAvatarGroup({
  users,
  userMap,
  maxAvatars = DEFAULT_MAX_VISIBLE_AVATARS,
}: {
  users: SupportTicketPresenceRecord[];
  userMap: Map<string, string>;
  maxAvatars?: number;
}) {
  const overlap = 16;
  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) => {
        if (a.isTyping !== b.isTyping) {
          return a.isTyping ? -1 : 1;
        }
        return a.userId.localeCompare(b.userId);
      }),
    [users],
  );
  const visible = sortedUsers.slice(0, maxAvatars);
  const overflow = users.length - visible.length;

  return (
    <Flex align="center">
      <Flex position="relative">
        {visible.map((user, index) => {
          const name = userMap.get(user.userId);
          const label = user.isTyping
            ? `${name ?? user.userId} is typing…`
            : `${name ?? user.userId} is viewing`;

          return (
            <Tooltip arrow key={user.id} title={label}>
              <div
                style={{
                  marginLeft: index === 0 ? 0 : -overlap,
                  position: "relative",
                  zIndex: maxAvatars - index,
                }}
              >
                <RedoAvatar
                  content={
                    name
                      ? { type: "text", text: name }
                      : { type: "icon", Icon: UserIcon }
                  }
                  size="xs"
                  status={user.isTyping ? { type: "typing" } : undefined}
                />
              </div>
            </Tooltip>
          );
        })}
      </Flex>
      {overflow > 0 && (
        <Text fontSize="xxs" ml="xs" textColor="tertiary">
          +{overflow}
        </Text>
      )}
    </Flex>
  );
});
